import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, Play, Eye, Send, Check, X, Trash2, Plus, ChevronDown, ChevronUp, Newspaper, Tag } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import OffersTab from '../components/posting/OffersTab';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const STATUS_STYLE = {
  ok: 'bg-green-100 text-green-700',
  awaiting_approval: 'bg-amber-100 text-amber-700',
  preview: 'bg-blue-100 text-blue-700',
  running: 'bg-blue-100 text-blue-700',
  skipped: 'bg-gray-100 text-gray-600',
  rejected: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
};
const STATUS_LABEL = {
  ok: 'Published', awaiting_approval: 'Awaiting approval', preview: 'Preview', running: 'Running',
  skipped: 'Nothing posted', rejected: 'Rejected', failed: 'Failed',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');

// "Wednesday 13:00 UTC" shown in the viewer's own time as well.
const localEquivalent = (dow, hour) => {
  const d = new Date(Date.UTC(2024, 0, 1 + Number(dow), Number(hour)));  // 2024-01-01 is a Monday
  return d.toLocaleString(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' });
};

const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[status] || 'bg-gray-100 text-gray-600'}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

const AdminPosting = () => {
  usePageTitle('Posting');
  const { userTheme } = useTheme();
  const primaryColor = userTheme?.primaryColor || '#2B4B6A';

  const [tab, setTab] = useState('weekly');
  const [overview, setOverview] = useState(null);
  const [form, setForm] = useState(null);
  const [runs, setRuns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [newTopic, setNewTopic] = useState('');
  const [editing, setEditing] = useState(null);  // { runId, index, title, excerpt, content }
  const [busy, setBusy] = useState('');

  const load = useCallback(async (resetForm = false) => {
    try {
      const [o, r] = await Promise.all([
        api.get('/admin/posting/weekly-news'),
        api.get('/admin/posting/runs', { params: { limit: 15 } }),
      ]);
      setOverview(o.data);
      setRuns(r.data.runs || []);
      setForm(prev => (resetForm || !prev ? o.data.settings : prev));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load posting settings');
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  // A run takes a few minutes; poll until it finishes.
  const running = overview?.running || starting;
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(async () => {
      await load(false);
      setStarting(false);
    }, 4000);
    return () => clearInterval(id);
  }, [running, load]);

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const dirty = form && overview && JSON.stringify(form) !== JSON.stringify(overview.settings);

  const save = async () => {
    try {
      setSaving(true);
      const res = await api.put('/admin/posting/weekly-news', form);
      setForm(res.data.settings);
      toast.success('Settings saved');
      await load(true);
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save — check the fields');
    } finally {
      setSaving(false);
    }
  };

  const startRun = async (action) => {
    if (dirty) { toast.error('Save your settings first so the run uses them'); return; }
    if (action === 'run' && form.mode === 'auto' &&
        !window.confirm('This will publish to the website and announcements' +
          (form.destinations.email ? ` and email ${overview.email_audience} users` : '') + '. Continue?')) return;
    try {
      setStarting(true);
      await api.post('/admin/posting/weekly-news/run', { action });
      toast.success(action === 'preview' ? 'Preview started — takes a few minutes' : 'Run started — takes a few minutes');
    } catch (error) {
      setStarting(false);
      toast.error(error.response?.data?.detail || 'Could not start the run');
    }
  };

  const sendTest = async (runId) => {
    try {
      setBusy('test');
      const res = await api.post('/admin/posting/weekly-news/send-test', { run_id: runId });
      toast.success(`Test email sent to ${res.data.sent_to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send the test');
    } finally {
      setBusy('');
    }
  };

  const approve = async (run) => {
    const n = form?.destinations?.email ? ` and email ${overview.email_audience} users` : '';
    if (!window.confirm(`Publish ${run.articles.length} article(s) to the website and announcements${n}?`)) return;
    try {
      setBusy('approve');
      await api.post(`/admin/posting/runs/${run.id || run._id}/approve`);
      toast.success('Published');
      await load(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not publish');
    } finally {
      setBusy('');
    }
  };

  const reject = async (run) => {
    try {
      await api.post(`/admin/posting/runs/${run._id}/reject`);
      toast.success('Draft rejected');
      await load(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reject');
    }
  };

  const saveArticle = async () => {
    try {
      await api.put(`/admin/posting/runs/${editing.runId}/articles/${editing.index}`,
        { title: editing.title, excerpt: editing.excerpt, content: editing.content });
      setEditing(null);
      toast.success('Article updated');
      await load(false);
    } catch (error) {
      toast.error('Could not save the article');
    }
  };

  const removeArticle = async (runId, index) => {
    try {
      await api.delete(`/admin/posting/runs/${runId}/articles/${index}`);
      await load(false);
    } catch (error) {
      toast.error('Could not remove the article');
    }
  };

  const addTopic = () => {
    const t = newTopic.trim();
    if (!t || form.topics.includes(t) || form.topics.length >= 12) return;
    set({ topics: [...form.topics, t] });
    setNewTopic('');
  };

  const pending = runs.find(r => ['awaiting_approval', 'preview'].includes(r.status) && !r.published_at && r.articles?.length);

  const input = 'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:outline-none';

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Posting</h1>
          <p className="text-gray-500 text-sm mt-1">Automated news and offers. Set what gets posted and when; a cron job runs it for you.</p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {[['weekly', 'Weekly news', Newspaper], ['offers', 'Monthly offers', Tag]].map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === id ? 'border-current' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              style={tab === id ? { color: primaryColor, borderColor: primaryColor } : {}}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {tab === 'offers' && <OffersTab primaryColor={primaryColor} />}

        {tab === 'weekly' && (!form || !overview ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : (
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-white border rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox" className="sr-only peer" checked={form.enabled}
                    onChange={e => set({ enabled: e.target.checked })}
                    aria-label="Enable weekly news"
                  />
                  <span className="w-11 h-6 rounded-full bg-gray-300 peer-checked:bg-green-500 relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-all peer-checked:after:translate-x-5" />
                  <span className="text-sm font-medium text-gray-800">{form.enabled ? 'Weekly news is on' : 'Weekly news is off'}</span>
                </label>
              </div>
              <div className="text-sm text-gray-600 space-y-0.5 sm:text-right">
                <div>Next run: <strong>{overview.settings.enabled && overview.next_run ? fmt(overview.next_run) : 'not scheduled'}</strong></div>
                <div>Last run: {overview.last_run ? <><StatusBadge status={overview.last_run.status} /> <span className="text-gray-500">{fmt(overview.last_run.started_at)}</span></> : 'never'}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <button onClick={() => startRun('preview')} disabled={running}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50">
                <Eye className="w-4 h-4" /> Preview (publishes nothing)
              </button>
              <button onClick={() => startRun('run')} disabled={running}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50" style={{ backgroundColor: primaryColor }}>
                <Play className="w-4 h-4" /> Run now ({form.mode === 'auto' ? 'publishes' : 'saves a draft'})
              </button>
              {running && <span className="flex items-center gap-2 text-sm text-blue-700"><Loader2 className="w-4 h-4 animate-spin" /> Researching and writing… this takes a few minutes</span>}
            </div>

            {/* Draft / preview review */}
            {pending && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{pending.status === 'preview' ? 'Preview' : 'Waiting for your approval'} — {pending.articles.length} article(s)</h3>
                    <p className="text-sm text-gray-600">Nothing is published until you approve. You can edit or remove articles first.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => sendTest(pending._id)} disabled={busy === 'test'}
                      className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white border hover:bg-gray-50 disabled:opacity-50">
                      {busy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test to me
                    </button>
                    <button onClick={() => reject(pending)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white border text-red-600 hover:bg-red-50">
                      <X className="w-4 h-4" /> Reject
                    </button>
                    <button onClick={() => approve({ ...pending, id: pending._id })} disabled={busy === 'approve'}
                      className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
                      {busy === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve &amp; publish
                    </button>
                  </div>
                </div>
                {pending.articles.map((a, i) => (
                  <div key={i} className="bg-white border rounded-lg p-4">
                    {editing && editing.runId === pending._id && editing.index === i ? (
                      <div className="space-y-2">
                        <input className={input} value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} aria-label="Title" />
                        <textarea className={input} rows={2} value={editing.excerpt} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} aria-label="Excerpt" />
                        <textarea className={`${input} font-mono`} rows={10} value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} aria-label="Content (HTML)" />
                        <div className="flex gap-2">
                          <button onClick={saveArticle} className="px-3 py-1.5 text-sm rounded-lg text-white" style={{ backgroundColor: primaryColor }}>Save</button>
                          <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm rounded-lg bg-gray-100">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">{a.commodity}</div>
                            <h4 className="font-semibold text-gray-900">{a.title}</h4>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button onClick={() => setEditing({ runId: pending._id, index: i, title: a.title, excerpt: a.excerpt, content: a.content })}
                              className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200">Edit</button>
                            <button onClick={() => removeArticle(pending._id, i)} className="p-1 text-gray-400 hover:text-red-600" aria-label="Remove article"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                        <div className="prose prose-sm max-w-none mt-2 text-gray-700" dangerouslySetInnerHTML={{ __html: a.content }} />
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Settings */}
            <div className="bg-white border rounded-xl p-5 space-y-5">
              <h3 className="font-semibold text-gray-900">Settings</h3>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">When it goes live</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[['auto', 'Auto-post', 'Publishes to the website, announcements and email by itself.'],
                    ['draft', 'Draft for my approval', 'Saves a draft. Nothing goes out until you approve it here.']].map(([v, t, d]) => (
                    <label key={v} className={`border rounded-lg p-3 cursor-pointer ${form.mode === v ? 'border-current bg-blue-50/40' : ''}`} style={form.mode === v ? { color: primaryColor } : {}}>
                      <input type="radio" name="mode" className="mr-2" checked={form.mode === v} onChange={() => set({ mode: v })} />
                      <span className="font-medium text-gray-900">{t}</span>
                      <p className="text-xs text-gray-500 mt-1 ml-5">{d}</p>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Schedule (the cron job checks hourly)</div>
                <div className="flex flex-wrap items-center gap-3">
                  <select className="px-3 py-2 border rounded-lg text-sm" value={form.schedule.day_of_week}
                    onChange={e => set({ schedule: { ...form.schedule, day_of_week: Number(e.target.value) } })} aria-label="Day of week">
                    {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                  <select className="px-3 py-2 border rounded-lg text-sm" value={form.schedule.hour}
                    onChange={e => set({ schedule: { ...form.schedule, hour: Number(e.target.value) } })} aria-label="Hour (UTC)">
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC</option>)}
                  </select>
                  <span className="text-sm text-gray-500">= {localEquivalent(form.schedule.day_of_week, form.schedule.hour)} your time</span>
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Topics to look for</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.topics.map(t => (
                    <span key={t} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-sm rounded-full pl-3 pr-1 py-1">
                      {t}
                      <button onClick={() => form.topics.length > 1 && set({ topics: form.topics.filter(x => x !== t) })}
                        className="p-0.5 rounded-full hover:bg-gray-200" aria-label={`Remove ${t}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input className={input} placeholder="Add a topic, e.g. Wheat" value={newTopic}
                    onChange={e => setNewTopic(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTopic()} />
                  <button onClick={addTopic} className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200"><Plus className="w-4 h-4" /> Add</button>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-sm text-gray-700">Articles per week
                  <select className={`${input} mt-1`} value={form.articles_per_run} onChange={e => set({ articles_per_run: Number(e.target.value) })}>
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label className="block text-sm text-gray-700">Minimum independent sources per story
                  <select className={`${input} mt-1`} value={form.min_sources} onChange={e => set({ min_sources: Number(e.target.value) })}>
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Where it is posted</div>
                <div className="space-y-2 text-sm text-gray-700">
                  {[['news', 'Latest News (website and admin news widget)'],
                    ['announcement', 'Announcements (one summary post)'],
                    ['email', `Email to all users (${overview.email_audience} active, verified users; unsubscribed users are skipped)`]].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2">
                      <input type="checkbox" checked={form.destinations[k]} onChange={e => set({ destinations: { ...form.destinations, [k]: e.target.checked } })} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <label className="block text-sm text-gray-700">Tone
                <textarea className={`${input} mt-1`} rows={2} value={form.tone} onChange={e => set({ tone: e.target.value })} />
              </label>
              <label className="block text-sm text-gray-700">Extra instructions (optional)
                <textarea className={`${input} mt-1`} rows={2} placeholder="e.g. Focus on Brazil and Argentina. Avoid political commentary." value={form.extra_instructions} onChange={e => set({ extra_instructions: e.target.value })} />
              </label>
              <label className="block text-sm text-gray-700">Email me if a run fails (blank = the default admin address)
                <input className={`${input} mt-1`} type="email" value={form.notify_email} onChange={e => set({ notify_email: e.target.value })} />
              </label>

              <div className="flex items-center justify-end gap-3">
                {dirty && <span className="text-sm text-amber-600">Unsaved changes</span>}
                <button onClick={save} disabled={saving || !dirty}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-40" style={{ backgroundColor: primaryColor }}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
                </button>
              </div>
            </div>

            {/* History */}
            <div className="bg-white border rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Run history</h3>
              {runs.length === 0 ? (
                <p className="text-sm text-gray-500">No runs yet. Try a preview.</p>
              ) : (
                <div className="divide-y">
                  {runs.map(r => (
                    <div key={r._id} className="py-2">
                      <button className="w-full flex flex-wrap items-center justify-between gap-2 text-left" onClick={() => setExpanded(expanded === r._id ? null : r._id)}>
                        <span className="flex items-center gap-3 text-sm">
                          <StatusBadge status={r.status} />
                          <span className="text-gray-800">{fmt(r.started_at)}</span>
                          <span className="text-gray-500">{r.trigger === 'schedule' ? 'scheduled' : 'manual'} · {r.mode}</span>
                        </span>
                        <span className="flex items-center gap-3 text-sm text-gray-500">
                          {r.articles?.length || 0} articles · ${(r.cost_usd || 0).toFixed(2)}
                          {expanded === r._id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </span>
                      </button>
                      {expanded === r._id && (
                        <div className="mt-2 ml-1 text-sm space-y-1 text-gray-600">
                          {r.error && <div className="text-red-700 bg-red-50 rounded p-2">{r.error}</div>}
                          {(r.steps || []).filter(s => s.status !== 'running').map((s, i) => (
                            <div key={i}>{s.status === 'failed' ? '❌' : s.status === 'warn' ? '⚠️' : '✅'} <strong>{s.name}</strong> — {s.detail}</div>
                          ))}
                          {(r.rejected || []).map((x, i) => <div key={i}>⏭️ Rejected by fact-check: {x.title}</div>)}
                          {(r.articles || []).map((a, i) => <div key={i} className="text-gray-800">• {a.title}</div>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Sidebar>
  );
};

export default AdminPosting;
